import React from 'react';
import throttle from 'lodash.throttle';
import { Scrollbar } from 'react-scrollbars-custom';
import { Text } from 'components/common';
import Sticky from 'react-sticky-el';
import { getTextContent } from 'utils/string';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import { KEYS } from 'constants/common';
import {
  TabWrapper,
  TabNavWrapper,
  TabNav,
  TabNavShadow,
  TabContentWrapper
} from './TabBar.styled';
import TabContent from './TabContent';

type TabBarProps = {
  content: string;
  inverted: boolean;
  childrenContents: any[];
  linkTabIndex?: number;
};

type TabBarState = {
  activeTab: number;
  isActiveTopShadow: boolean;
  isActiveBottomShadow: boolean;
  isDisabledStickyBar: boolean;
};

const TAB_BAR_NAV_HEIGHT = '550px';
const RESOURCE_SCROLL_CONTAINER = '.resource-scroll-container';

class TabBar extends React.PureComponent<TabBarProps, TabBarState> {
  constructor(props: TabBarProps) {
    super(props);
    this.state = {
      activeTab: 0,
      isActiveTopShadow: false,
      isActiveBottomShadow: false,
      isDisabledStickyBar: true
    };
  }

  setActiveTab(index: number) {
    this.setState({ activeTab: index });
    this.disabledStickyBar();

    const stickyBar: HTMLDivElement | null = document.querySelector('.sticky');
    const resourceScrollContainer: HTMLDivElement | null = document.querySelector(
      RESOURCE_SCROLL_CONTAINER
    );
    const tabWrapper: HTMLDivElement | null = document.querySelector('.tab-wrapper');
    if (stickyBar && resourceScrollContainer && tabWrapper) {
      const { top } = stickyBar.getBoundingClientRect();
      if (top === 0) {
        resourceScrollContainer.scrollTop = tabWrapper.offsetTop;
      }
    }
  }

  keyDownHandler = (event: any, index: any) => {
    if ([KEYS.ENTER.toLowerCase(), KEYS.SPACEBAR.toLowerCase()].includes(event.key.toLowerCase())) {
      this.setActiveTab(index);
    }
  };

  renderTabNav() {
    const { activeTab } = this.state;
    const { childrenContents, linkTabIndex } = this.props;
    const tabIndex = linkTabIndex || linkTabIndex === 0 ? linkTabIndex : 1;
    return childrenContents.map((content: { id: any; value: any }, index: number) => {
      const { id, value } = content;
      return (
        <TabNav
          isActive={index === activeTab}
          key={id}
          onClick={() => this.setActiveTab(index)}
          onKeyDown={event => this.keyDownHandler(event, index)}
          tabIndex={tabIndex}
        >
          <Text appearance="h4" size={18}>
            {getTextContent(value)}
          </Text>
        </TabNav>
      );
    });
  }

  renderTabContent() {
    const { activeTab } = this.state;
    const { childrenContents, inverted, linkTabIndex } = this.props;
    const tab = childrenContents[activeTab];
    const { id, value, children } = tab;
    return (
      <SwitchTransition mode="out-in">
        <CSSTransition
          key={id}
          timeout={100}
          addEndListener={(node, done) => {
            node.addEventListener('transitionend', done, false);
          }}
        >
          <TabContent
            key={id}
            itemId={id}
            content={value}
            childrenElements={children}
            inverted={inverted}
            linkTabIndex={linkTabIndex}
          />
        </CSSTransition>
      </SwitchTransition>
    );
  }

  setTabNavShadowState({ clientHeight, contentScrollHeight, scrollTop }: any) {
    if (clientHeight === contentScrollHeight) {
      return;
    }
    throttle(() => {
      const isActiveTopShadow = scrollTop >= 10;
      const isActiveBottomShadow = clientHeight + scrollTop <= contentScrollHeight - 10;
      this.setState({ isActiveTopShadow, isActiveBottomShadow });
    }, 300)();
  }

  disabledStickyBar() {
    const tabWrapper: HTMLDivElement | null = document.querySelector('.tab-wrapper');
    const stickyWrapper: HTMLDivElement | null = document.querySelector('.sticky-wrapper');

    if (tabWrapper && stickyWrapper) {
      if (tabWrapper.offsetHeight >= stickyWrapper.offsetHeight) {
        this.setState({ isDisabledStickyBar: false });
      } else {
        this.setState({ isDisabledStickyBar: true });
      }
    }
  }

  componentDidMount() {
    const scrollContainer: HTMLDivElement | null = document.querySelector(
      RESOURCE_SCROLL_CONTAINER
    );
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', () => this.disabledStickyBar());
    }
  }

  componentWillUnmount() {
    const scrollContainer: HTMLDivElement | null = document.querySelector(
      RESOURCE_SCROLL_CONTAINER
    );
    if (scrollContainer) {
      scrollContainer.removeEventListener('scroll', () => this.disabledStickyBar());
    }
  }

  render() {
    const { isActiveTopShadow, isActiveBottomShadow, isDisabledStickyBar } = this.state;
    return (
      <TabWrapper className="tab-wrapper">
        <TabNavWrapper className="tab-nav-wrapper">
          <Sticky
            className="sticky-wrapper"
            scrollElement={RESOURCE_SCROLL_CONTAINER}
            boundaryElement=".tab-wrapper"
            disabled={isDisabledStickyBar}
          >
            <TabNavShadow isActive={isActiveTopShadow} />
            <Scrollbar
              onUpdate={data => this.setTabNavShadowState(data)}
              style={{ height: TAB_BAR_NAV_HEIGHT }}
              noScrollX={false}
            >
              {this.renderTabNav()}
            </Scrollbar>
            <TabNavShadow className="bottom-line" isActive={isActiveBottomShadow} />
          </Sticky>
        </TabNavWrapper>
        <TabContentWrapper>{this.renderTabContent()}</TabContentWrapper>
      </TabWrapper>
    );
  }
}

export default TabBar;
